import React, { Fragment, useState } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid'
import { Label } from 'src/components'
import { classes } from 'src/utils'

export type SelectOption<K = string> = { key: K; name: string }

type Props<K, V> = {
  title?: string | undefined
  id?: K
  value: V | undefined
  displayOptionValue?: boolean
  onChange: (value: V, id?: K) => void
  options: Array<SelectOption<V>>
  className?: string
  placeholder?: string
  classNameLabel?: string
  disabled?: boolean
}

export function Select<K = string, V = string>({
  classNameLabel,
  placeholder = 'please choose an option from the list',
  ...props
}: Props<K, V>) {
  const [selected, setSelected] = useState(
    props.options.find((op) => op.key === props.value),
  )

  const onChange = (option: SelectOption<V>) => {
    setSelected(option)
    props.onChange(option.key, props.id)
  }

  return (
    <div className={classes('group', props.className)}>
      {props.title ? (
        <Label
          className={classes(
            'group-hover:text-slate-500 group-focus-within:text-theme-500 group-hover:group-focus-within:text-theme-500 mb-1 ',
            classNameLabel,
          )}
        >
          {props.title}
        </Label>
      ) : null}
      <Listbox value={selected} onChange={onChange} disabled={props.disabled}>
        <div
          className={classes('relative', props.disabled ? 'opacity-50' : '')}
        >
          <Listbox.Button
            aria-required
            className="relative flex w-full pl-3 pr-10 flex-row items-center h-10 bg-white border rounded text-slate-600 border-slate-100 overflow-hidden active:shadow-none focus-within:border-theme-500 outline-none focus-within:ring-theme-500 focus-within:ring-1 hover:not(:disabled):shadow-sm hover:not(:disabled):border-slate-200 hover:focus-within:border-theme-500"
          >
            <span
              className={classes('block truncate', {
                'text-slate-400': !selected,
              })}
            >
              {selected ? selected.name : placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <SelectorIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute mt-1 z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {props.options.map((option, index) => (
                <Listbox.Option
                  key={index}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? 'bg-theme-500 text-white' : 'text-gray-900'
                    }`
                  }
                  value={option}
                >
                  {({ selected, active }) => (
                    <div className="flex flex-row">
                      <span
                        className={`block truncate flex-1 ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}
                      >
                        {option.name}
                      </span>
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                          <CheckIcon
                            className={`h-5 w-5 ${
                              active ? 'fill-white' : 'fill-theme-500'
                            }`}
                            aria-hidden="true"
                          />
                        </span>
                      ) : null}
                      {props.displayOptionValue ? (
                        <span
                          className={`block truncate ${
                            selected ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          {option.key}
                        </span>
                      ) : null}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  )
}
